import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OperadoraCompetenciaParams } from '@gestor-web/@core/types';
import { HEADER_SKIP_LOAD_INTERCEPTOR } from '@gestor-web/app/shared/services';
import { environment } from '@gestor-web/environments/environment';
import { Api } from '../api';
import {
  ApiResponse,
  ArquivoGerado,
  ArquivoPostado,
  ArquivoSituacaoDiops,
  ArquivoSituacaoSib,
  ArquivoSituacaoTiss,
  ArquivosPostados,
  BasicResponse,
  DetalhesLotesXteDialog,
  LotesXte,
  LotesXtePendentes,
  PAGE_PARAMS_DEFAULT,
  PageParams,
  PreVerificacaoRequest,
  PreVerificacaoResponse,
  TipoArquivo,
  UploadMetadata,
  ValidacaoLotesXte,
} from '../ords/model';

const MODULE_PATH = '/simulador/arquivos';

@Injectable({ providedIn: 'root' })
export class ArquivoGoApi extends Api {
  constructor(http: HttpClient) {
    super(`${environment.API_GO_BASE_PATH}${MODULE_PATH}`, http);
  }

  checkPreVerificacaoPostagem({ regans, ...metadata }: PreVerificacaoRequest) {
    return this.post<PreVerificacaoResponse>(`/pre-verificacao-postagem/operadora/${regans}`, metadata);
  }

  putPostagemArquivo(regans: string, params: UploadMetadata, file: File) {
    // console.log('[GoApi] putPostagemArquivo', params);
    const formData = new FormData();
    formData.append('cd_arquivo', params.cd_arquivo + '');
    formData.append('nm_arquivo', params.nm_arquivo);
    formData.append('ds_hash_arquivo', params.ds_hash_arquivo);
    formData.append('tp_postagem', 'gestor-idss');
    formData.append('file', file);
    return this.put<BasicResponse>(`/operadora/${regans}/postar`, formData);
  }

  downloadSimuladoPdfANS(regans: string, cd_simulado: number) {
    const path = `/operadora/${regans}/${cd_simulado}/baixar`;
    return this.http.get(`${this.basePath}${path}`, {
      responseType: 'blob' as 'json',
    });
  }

  /**
   * Realiza download do arquivo informado.
   *
   * Reporta o progresso para notificar para o usuário.
   *
   * Desativa load interceptor para não ser cancelado enquanto o usuário navega em outras telas.
   *
   * O back responde 302 para a URL pré-autenticada do bucket; o navegador a segue sozinho.
   *
   * Inativo: o Go só serve arquivos do bucket, e ainda há rotinas gravando em disco. Até elas
   * migrarem, o download vai pelo `SimuladorApi` (ORDS), que lê dos dois.
   */
  downloadArquivoGerado(regans: string, arquivo: ArquivoGerado) {
    return this.http.get(`${this.basePath}/operadora/${regans}/downloads/${arquivo.cd_arquivo}`, {
      observe: 'events',
      responseType: 'blob',
      reportProgress: true,
      headers: { [HEADER_SKIP_LOAD_INTERCEPTOR]: 'true' },
    });
  }

  /**
   * Baixa o log do lote XTE informado.
   *
   * Inativo pelo mesmo motivo do `downloadArquivoGerado`.
   */
  downloadLoteXte(regans: string, arquivo: ValidacaoLotesXte) {
    return this.http.get(
      `${this.basePath}/operadora/${regans}/lotes-xte/downloads-log/${arquivo.cd_arquivo}`,
      {
        responseType: 'blob',
      },
    );
  }

  fetchArquivosSituacaoTiss(regans: string, anoBase: number) {
    return this.get<ApiResponse<ArquivoSituacaoTiss>>(`/operadora/${regans}/situacao/${anoBase}/tiss`);
  }

  fetchArquivosSituacaoDiops(regans: string, anoBase: number) {
    return this.get<ApiResponse<ArquivoSituacaoDiops>>(`/operadora/${regans}/situacao/${anoBase}/diops`);
  }

  fetchArquivosSituacaoSib(regans: string, anoBase: number) {
    return this.get<ApiResponse<ArquivoSituacaoSib>>(`/operadora/${regans}/situacao/${anoBase}/sib`);
  }

  fetchLotesXtePendentes(regans: string, anoBase: number) {
    return this.get<ApiResponse<LotesXtePendentes>>(`/operadora/${regans}/lotes-xte-pendentes/${anoBase}`);
  }

  fetchValidacaoLotesXte({ regans, ...params }: OperadoraCompetenciaParams & { dt_inclusao_desde: string }) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<ValidacaoLotesXte>>(`/operadora/${regans}/validacao-lotes-xte`, trimmed);
  }

  fetchLotesXte({ regans, ...params }: OperadoraCompetenciaParams) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<LotesXte>>(`/operadora/${regans}/lotes-xte`, trimmed);
  }

  fetchDetalhesLotesXte({
    regans,
    nr_competencia,
    nr_lote,
    cd_arquivo_envio,
    cd_arquivo_retorno,
  }: {
    regans: string;
    nr_competencia: number;
    nr_lote?: number;
    cd_arquivo_envio: number;
    cd_arquivo_retorno: number;
  }) {
    const trimmed = this.trimParams({ nr_competencia, nr_lote, cd_arquivo_envio, cd_arquivo_retorno });
    return this.get<ApiResponse<DetalhesLotesXteDialog>>(`/operadora/${regans}/detalhes/lotes-xte`, trimmed);
  }

  fetchArquivosPostados(regans: string, dt_inclusao_desde: string) {
    return this.get<ApiResponse<ArquivosPostados>>(
      `/operadora/${regans}/arquivos-postados?dt_inclusao_desde=${dt_inclusao_desde}`,
    );
  }

  fetchConteudoArquivo(regans: string, cd_arquivo: string) {
    return this.get<ApiResponse<ArquivosPostados>>(`/operadora/${regans}/arquivos-postados/${cd_arquivo}`);
  }

  fetchArquivosGerados(regans: string, dt_inclusao_desde: string) {
    const params = this.trimParams({ dt_inclusao_desde });
    return this.get<ApiResponse<ArquivoGerado>>(`/operadora/${regans}/downloads`, params);
  }

  fetchArquivosOperadora(
    regans: string,
    tp_arquivo: TipoArquivo,
    dt_inclusao_desde: string,
    page: PageParams = PAGE_PARAMS_DEFAULT,
  ) {
    const params = this.trimParams({
      tp_arquivo,
      dt_inclusao_desde,
      page: Math.floor(page.offset / page.limit),
      size: page.limit,
    });
    return this.get<ApiResponse<ArquivoPostado>>(`/operadora/${regans}`, params);
  }
}
